import { useState } from 'react';
import StepOne from './StepOne';
import StepTwo from './StepTwo';

export default function BehavioralHealthAssessment() {
  const [current, setCurrent] = useState(0);
  const [isStep1Saved, setIsStep1Saved] = useState(false);
  const [isStep2Saved, setIsStep2Saved] = useState(false);
  const [showIntro, setShowIntro] = useState(true);
  const [step2NavState, setStep2NavState] = useState({
    canContinue: true,
    showWelcome: true,
    currentQuestion: 0,
    totalQuestions: 3
  });

  const steps = [
    { title: "Personal", subtitle: "Information" },
    { title: "Emotional", subtitle: "Patterns" },
    { title: "Your", subtitle: "Results" },
  ];

  const getProgressPercent = () => {
    if (current === 0) return isStep1Saved ? 33 : 0;
    if (current === 1) return isStep2Saved ? 67 : 33;
    if (current === 2) return 100;
    return 0;
  };

  const progressPercent = getProgressPercent();

  const handleBegin = () => {
    if (current === 0) {
      setShowIntro(false);
    } else if (current === 1) {
      if (window.step2Methods) window.step2Methods.handleStart();
    }
  };

  const next = () => {
    if (current === 0 && !showIntro) {
      setIsStep1Saved(true);
      setCurrent(1);
    } else if (current === 1) {
      if (window.step2Methods) window.step2Methods.handleContinue();
    } else {
      setCurrent(prev => prev + 1);
    }
  };

  const prev = () => {
    if (current === 0 && !showIntro) {
      setShowIntro(true);
    } else if (current === 1) {
      if (window.step2Methods) window.step2Methods.handlePrevious();
    } else if (current > 1) {
      setCurrent(current - 1);
    }
  };

  const getStepStatus = (index) => {
    if (index < current) {
      if (index === 0) return isStep1Saved ? 'complete' : 'current';
      if (index === 1) return isStep2Saved ? 'complete' : 'current';
      return 'complete';
    }
    if (index === current) return index === 2 ? 'complete' : 'current';
    return 'upcoming';
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" style={{ color: '#132573' }}>
          Behavioral Health Assessment
        </h2>

        {/* Steps Progress */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-8">
            {steps.map((step, index) => {
              const status = getStepStatus(index);
              return (
                <div key={index} className="flex flex-col items-center flex-1">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center font-semibold mb-2 transition-all duration-300 ${
                    status === 'complete' 
                      ? 'bg-green-500 text-white' 
                      : status === 'current'
                      ? 'bg-blue-600 text-white ring-4 ring-blue-200'
                      : 'bg-gray-300 text-gray-600'
                  }`}>
                    {status === 'complete' ? (
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      index + 1
                    )}
                  </div>
                  
                  <div className="text-center text-xs md:text-sm">
                    <div className={`font-semibold ${status === 'current' ? 'text-blue-600' : 'text-gray-700'}`}>
                      {step.title}
                    </div>
                    <div className={`${status === 'current' ? 'text-blue-600' : 'text-gray-600'}`}>
                      {step.subtitle}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="text-center mb-2 text-sm font-semibold" style={{ color: '#132573' }}>
            {Math.round(progressPercent)}% Complete
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
            <div 
              className="h-full transition-all duration-500 rounded-full"
              style={{ 
                width: `${progressPercent}%`,
                background: 'linear-gradient(90deg, #132573 0%, #3b82f6 100%)'
              }}
            />
          </div>
        </div>

        {/* Content Area */}
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 mb-24">
          {/* Step 1 Intro */}
          {showIntro && current === 0 && (
            <div className="text-center max-w-2xl mx-auto">
              <h3 className="text-2xl md:text-3xl font-bold mb-6" style={{ color: '#132573' }}>
                Let's begin by getting to know you.
              </h3>
              <p className="text-gray-700 text-lg leading-relaxed">
                We will ask for your basic information such as name, email address and age
                to start building your personalized behavioral health assessment.
              </p>
            </div>
          )}

          {/* Step 1 Form */}
          {!showIntro && current === 0 && <StepOne onSave={() => setIsStep1Saved(true)} />}

          {/* Step 2 */}
          {current === 1 && (
            <StepTwo
              onComplete={(answers) => {
                setIsStep2Saved(true);
                console.log("Step 2 Completed:", answers);
                setCurrent(2);
              }}
              onAnswerUpdate={(answers) => console.log("Step 2 updated:", answers)}
              onNavigationStateChange={setStep2NavState}
            />
          )}

          {/* Step 3 - Results */}
          {current === 2 && (
            <div className="text-center max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-8" style={{ color: '#132573' }}>
                Time for your results
              </h2>

              <div className="bg-blue-50 rounded-xl p-8 mb-6" style={{ borderWidth: '2px', borderColor: '#132573' }}>
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  Thank you for taking the time to complete the Behavioral Health Assessment.
                  Your complimentary results summary is ready and available below.
                </p>

                <p className="text-gray-700 text-lg leading-relaxed">
                  Based on your responses, this summary will help you understand your current mental health status
                  and provide recommendations for support. For a more comprehensive and personalized
                  analysis, please consult with a mental health professional.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Fixed Footer Buttons */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg py-4 px-4 z-50">
          <div className="max-w-5xl mx-auto flex justify-between gap-4">
            {/* Step 1 Intro */}
            {showIntro && current === 0 && (
              <button
                onClick={handleBegin}
                className="ml-auto px-8 py-3 rounded-lg font-semibold text-white transition-all duration-300 hover:opacity-90"
                style={{ backgroundColor: '#132573' }}
              >
                Begin this section
              </button>
            )}

            {/* Step 1 Form */}
            {!showIntro && current === 0 && (
              <>
                <button
                  onClick={prev}
                  className="px-8 py-3 rounded-lg font-semibold border-2 transition-all duration-300 hover:bg-gray-50"
                  style={{ borderColor: '#132573', color: '#132573' }}
                >
                  Previous
                </button>
                <button
                  onClick={async () => {
                    if (window.step1Methods) {
                      const isValid = await window.step1Methods.validateForm();
                      if (isValid) {
                        const formData = window.step1Methods.getFormData();
                        console.log("Step 1 Form Data:", formData);
                        setIsStep1Saved(true);
                        next();
                      }
                    } else {
                      next();
                    }
                  }}
                  className="px-8 py-3 rounded-lg font-semibold text-white transition-all duration-300 hover:opacity-90"
                  style={{ backgroundColor: '#132573' }}
                >
                  Continue
                </button>
              </>
            )}

            {/* Step 2 Welcome */}
            {current === 1 && step2NavState.showWelcome && (
              <>
                <button
                  onClick={prev}
                  className="px-8 py-3 rounded-lg font-semibold border-2 transition-all duration-300 hover:bg-gray-50"
                  style={{ borderColor: '#132573', color: '#132573' }}
                >
                  Previous
                </button>
                <button
                  onClick={handleBegin}
                  className="px-8 py-3 rounded-lg font-semibold text-white transition-all duration-300 hover:opacity-90"
                  style={{ backgroundColor: '#f88226' }}
                >
                  Continue
                </button>
              </>
            )}

            {/* Step 2 Questions */}
            {current === 1 && !step2NavState.showWelcome && (
              <>
                <button
                  onClick={prev}
                  className="px-8 py-3 rounded-lg font-semibold border-2 transition-all duration-300 hover:bg-gray-50"
                  style={{ borderColor: '#132573', color: '#132573' }}
                >
                  Previous
                </button>
                <button
                  onClick={next}
                  disabled={!step2NavState.canContinue}
                  className="px-8 py-3 rounded-lg font-semibold text-white transition-all duration-300 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: '#132573' }}
                >
                  Continue
                </button>
              </>
            )}

            {/* Step 3 - Results */}
            {current === 2 && (
              <button
                onClick={() => console.log("View detailed results")}
                className="ml-auto px-8 py-3 rounded-lg font-semibold text-white transition-all duration-300 hover:opacity-90"
                style={{ backgroundColor: '#132573' }}
              >
                Get Detailed Results
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}